/**
 * Wave 3 SEO cluster (roles): rewrite the PDF you have for a job family.
 * Spanish and English. Merged from posts.js. Mid CTA variant `mid`.
 */

export default [
  {
    slug: 'cv-para-community-manager',
    date: '2026-08-22',
    category: 'career',
    related: ['adaptar-cv-a-una-oferta', 'palabras-clave-cv-ats', 'cv-canva-y-ats'],
    es: {
      title: 'CV de community manager: métricas, no solo “redes”',
      description:
        'Cómo reescribir el CV de community que ya tienes: canales, cifras y un PDF que el ATS lee. Canva no sustituye Meta Ads. ClarCV.',
      readMinutes: 12,
      sections: [
        {
          type: 'p',
          text: 'Un CV de community manager no se gana con el logo de Instagram en la cabecera. El ATS busca Meta Ads, Community Management, Hootsuite, “respuesta en 2 h”, no un pictograma. Quien busca este título suele tener un Canva bonito y viñetas de “gestión de redes sociales”. El reclutador de performance quiere alcance, engagement, crisis y, si aplica, ROAS. Esta guía reescribe el archivo que ya tienes, no una plantilla en blanco.',
        },
        {
          type: 'p',
          text: 'Hermanas: [adaptar el CV a una oferta](/blog/adaptar-cv-a-una-oferta) y [palabras clave ATS](/blog/palabras-clave-cv-ats). Si el PDF es de dos columnas, [Canva y ATS](/blog/cv-canva-y-ats). ClarCV (clarcv.com): subes el Word o PDF, ves estructura y claridad, regeneras plantilla A4 parseable. Gratis: 3 análisis y 2 descargas / 30 días, sin tarjeta. Pro 7 €/mes.',
        },
        {
          type: 'p',
          text: 'No inventes 200k seguidores. Pon el canal, el periodo y un número que puedas abrir en el panel. En entrevista te pedirán la captura.',
        },
        { type: 'h2', text: 'Viñetas que sobreviven a un screening' },
        {
          type: 'p',
          text: 'Antes: “Encargado de redes y creación de contenido creativo.” Después: “Community Instagram + TikTok (marca D2C, 2023–2025): calendario en Metricool, primera respuesta &lt; 2 h, 12 crisis de comentarios documentadas, +38% de alcance medio en 6 meses (dato de Insights).” Misma persona. Solo la segunda tiene keywords y prueba.',
        },
        {
          type: 'ul',
          items: [
            'Canales nombrados: Instagram, TikTok, LinkedIn, X, newsletter — los que tocaste.',
            'Herramientas: Meta Business Suite, Ads Manager, GA4, Canva (como herramienta, no como plantilla del CV), Notion.',
            'Volumen: posts/semana, tamaño de comunidad, idiomas de atención.',
            'Paid vs organic: no mezcles un boost de 50 € con “campañas performance”.',
            'Crisis y moderación: sí, es trabajo; una línea basta.',
          ],
        },
        { type: 'h2', text: 'Lo que rompe el ATS en CVs de marketing' },
        {
          type: 'p',
          text: 'Iconos de redes sin la palabra “Instagram”, barras de “creatividad 90%”, un QR al portfolio que el parser no sigue. El portfolio (Behance, web) va en texto en contacto. El CV del portal es una columna, encabezados Experiencia / Habilidades. Guía: [iconos en el CV](/blog/iconos-en-el-cv).',
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'El logo de Instagram no es una keyword',
          lead: 'Si tus canales son pictogramas, el ATS no extrae Meta Ads ni Community. Sube el PDF y deja canales y cifras en texto.',
          from: 'Iconos de redes',
          to: 'Instagram + cifra en texto',
          button: 'Reescribir mi CV de community',
        },
        { type: 'h2', text: 'Cómo recortar el archivo que ya tienes' },
        {
          type: 'p',
          text: 'Quédate con 3–4 logros por rol reciente. Cursos Udemy de “redes”: máximo uno si hay proyecto; ver [cursos online](/blog/cursos-online-en-el-cv). Resume de 3 líneas: tipo de marca (B2B/B2C), canales, un número. Luego ClarCV para formato parseable. Tú validas las métricas; el modelo no debe inventar un ROAS.',
        },
        {
          type: 'faq',
          items: [
            {
              q: '¿Pongo mis cuentas personales?',
              a: 'Solo si son el portfolio del rol (creador). Si no, LinkedIn profesional y la marca para la que trabajaste.',
            },
            {
              q: '¿Community y social ads van en el mismo CV?',
              a: 'Sí si los hiciste. Separa viñetas organic vs paid. Un CV “todo social” genérico pierde las dos ofertas.',
            },
            {
              q: '¿El ATS entiende “RR. SS.”?',
              a: 'Mejor el nombre del canal y “community management”. La abreviatura sola es débil.',
            },
            {
              q: '¿ClarCV es una herramienta de Meta?',
              a: 'No. Prepara el PDF. No accede a tus anuncios.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
    en: {
      title: 'Community manager CV: metrics, not just “social”',
      description:
        'How to rewrite the community CV you already have: channels, numbers, and a PDF ATS can read. Canva is not Meta Ads. ClarCV.',
      readMinutes: 12,
      sections: [
        {
          type: 'p',
          text: 'A community manager CV is not won with an Instagram logo in the header. ATS looks for Meta Ads, Community Management, Hootsuite, “reply in 2 h”, not a pictogram. People searching this title often have pretty Canva and “managed social media” bullets. A performance recruiter wants reach, engagement, crises, and ROAS if it applies. This guide rewrites the file you already have, not a blank template.',
        },
        {
          type: 'p',
          text: 'Siblings: [tailor your CV to a job](/blog/adaptar-cv-a-una-oferta) and [ATS keywords](/blog/palabras-clave-cv-ats). If the PDF is two columns, [Canva and ATS](/blog/cv-canva-y-ats). ClarCV (clarcv.com): upload Word or PDF, see structure and clarity, regenerate a parseable A4 template. Free: 3 analyses and 2 downloads / 30 days, no card. Pro €7/month.',
        },
        {
          type: 'p',
          text: 'Do not invent 200k followers. Put the channel, the period, and a number you can open in the dashboard. In interview they will ask for the screenshot.',
        },
        { type: 'h2', text: 'Bullets that survive a screen' },
        {
          type: 'p',
          text: 'Before: “In charge of social and creative content.” After: “Instagram + TikTok community (D2C brand, 2023–2025): Metricool calendar, first reply &lt; 2 h, 12 documented comment crises, +38% average reach in 6 months (Insights).” Same person. Only the second has keywords and proof.',
        },
        {
          type: 'ul',
          items: [
            'Named channels: Instagram, TikTok, LinkedIn, X, newsletter — the ones you touched.',
            'Tools: Meta Business Suite, Ads Manager, GA4, Canva (as a work tool, not as the CV template), Notion.',
            'Volume: posts/week, community size, support languages.',
            'Paid vs organic: do not mix a €50 boost with “performance campaigns”.',
            'Crises and moderation: yes, it is the job; one line is enough.',
          ],
        },
        { type: 'h2', text: 'What breaks ATS on marketing CVs' },
        {
          type: 'p',
          text: 'Social icons without the word “Instagram”, “creativity 90%” bars, a QR to a portfolio the parser never follows. The portfolio (Behance, site) goes as text in contact. The portal CV is one column, Experience / Skills headings. Guide: [icons on the CV](/blog/iconos-en-el-cv).',
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'The Instagram logo is not a keyword',
          lead: 'If your channels are pictograms, ATS will not extract Meta Ads or Community. Upload the PDF and leave channels and figures as text.',
          from: 'Social icons',
          to: 'Instagram + a number in text',
          button: 'Rewrite my community CV',
        },
        { type: 'h2', text: 'How to trim the file you already have' },
        {
          type: 'p',
          text: 'Keep 3–4 achievements per recent role. Udemy “social” courses: one max if there is a project; see [online courses](/blog/cursos-online-en-el-cv). Three-line summary: brand type (B2B/B2C), channels, one number. Then ClarCV for parseable layout. You validate the metrics; the model must not invent ROAS.',
        },
        {
          type: 'faq',
          items: [
            {
              q: 'Should I list personal accounts?',
              a: 'Only if they are the role’s portfolio (creator). If not, professional LinkedIn and the brand you worked for.',
            },
            {
              q: 'Can community and social ads live on the same CV?',
              a: 'Yes if you did both. Split organic vs paid bullets. A generic “all social” CV loses both jobs.',
            },
            {
              q: 'Does ATS understand “socials”?',
              a: 'Better the channel name and “community management”. The abbreviation alone is weak.',
            },
            {
              q: 'Is ClarCV a Meta tool?',
              a: 'No. It prepares the PDF. It does not access your ads.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
  },
  {
    slug: 'cv-para-comercial',
    date: '2026-08-22',
    category: 'career',
    related: ['experiencia-laboral-con-logros', 'cv-para-inditex', 'verbos-de-accion-cv'],
    es: {
      title: 'CV comercial: cifras que se pueden defender',
      description:
        'Reescribe el CV de ventas que ya tienes: cuota, ciclo, CRM y un PDF parseable. Sin “orientado a objetivos” vacío. ClarCV.',
      readMinutes: 12,
      sections: [
        {
          type: 'p',
          text: 'Un CV comercial sin cifras es un folleto. El ATS busca Salesforce, cartera, B2B, “cuota”, Hunter o Farmer según el anuncio. El director comercial busca % sobre objetivo, ticket medio, ciclo y si eras hunter o account. “Orientado a resultados” no es una keyword. Esta guía parte del PDF que ya tienes: convierte tareas en números honestos.',
        },
        {
          type: 'p',
          text: 'Cómo escribir logros: [logros en el CV](/blog/experiencia-laboral-con-logros). Verbos: [verbos de acción](/blog/verbos-de-accion-cv). ClarCV: subes el archivo, claridad y estructura, plantilla ATS. Gratis: 3 análisis y 2 descargas / 30 días, sin tarjeta. Pro 7 €/mes. Tú pones las cifras; no inventamos una cuota del 140%.',
        },
        { type: 'h2', text: 'Qué número poner (si no tienes “el dato oficial”)' },
        {
          type: 'p',
          text: 'Antes: “Responsable de captación y fidelización de clientes.” Después: “Cartera 45 cuentas B2B (SaaS, ciclo 90 días) en HubSpot; 112% de cuota 2024 (dato interno, orden de magnitud); 8 deals nuevos hunter.” Si no puedes decir el % exacto, usa proxies: número de visitas, demos/semana, zona, tipo de cliente. En entrevista tendrás que sostenerlo.',
        },
        {
          type: 'ul',
          items: [
            'Cuota o ranking de equipo si es defendible.',
            'CRM nombrado: Salesforce, HubSpot, Dynamics — el del anuncio si lo usaste.',
            'Hunter vs farmer vs inside: una palabra en el resumen.',
            'Producto: no “soluciones”; el sector (industrial, seguros, SaaS).',
            'Idioma de venta: [MCER](/blog/idiomas-en-el-cv) si hay clientes internacionales.',
          ],
        },
        { type: 'h2', text: 'Canva de “comercial star” y el portal' },
        {
          type: 'p',
          text: 'La plantilla con gráfico de barras de ventas es un dibujo. El ATS no extrae el 112%. Pon el número en texto junto al puesto. Una columna. Guía Canva si el PDF no se pega en el bloc de notas.',
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'La cuota tiene que ser texto, no un gráfico',
          lead: 'Si tus ventas son un dibujo de Canva, el filtro no las ve. Sube el CV y deja CRM y cifras en palabras.',
          from: 'Gráfico de ventas',
          to: 'Cuota y CRM en texto',
          button: 'Rehacer mi CV comercial',
        },
        { type: 'h2', text: 'Adapta hunter y farmer' },
        {
          type: 'p',
          text: 'No uses el mismo resumen para una vacante de captación pura y una de KAM. [Adapta 20 minutos](/blog/adaptar-cv-a-una-oferta). Recorta roles antiguos de retail si aplicas a enterprise, o al revés. Dos páginas máximo; el comercial senior denso compacta 2010–2016.',
        },
        {
          type: 'faq',
          items: [
            {
              q: '¿Puedo poner el nombre de clientes?',
              a: 'Solo si el contrato lo permite. Si no: “retail IBEX” o “3 cuentas >500 empleados”.',
            },
            {
              q: '¿Pongo la retribución variable?',
              a: 'No en el PDF. En entrevista, sí. El archivo viaja.',
            },
            {
              q: '¿Inside sales y field en el mismo CV?',
              a: 'Sí, etiquetados. El anuncio suele pedir uno.',
            },
            {
              q: '¿ClarCV calcula mi cuota?',
              a: 'No. Conserva lo que está en el archivo y el formato parseable.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
    en: {
      title: 'Sales CV: numbers you can defend',
      description:
        'Rewrite the sales CV you already have: quota, cycle, CRM, and a parseable PDF. No empty “results-oriented”. ClarCV.',
      readMinutes: 12,
      sections: [
        {
          type: 'p',
          text: 'A sales CV without numbers is a brochure. ATS looks for Salesforce, book of business, B2B, “quota”, hunter or farmer as the ad says. A sales director looks for % of target, average deal, cycle, and whether you hunted or farmed. “Results-oriented” is not a keyword. This guide starts from the PDF you already have: turn tasks into honest numbers.',
        },
        {
          type: 'p',
          text: 'How to write achievements: [CV achievements](/blog/experiencia-laboral-con-logros). Verbs: [action verbs](/blog/verbos-de-accion-cv). ClarCV: upload the file, clarity and structure, ATS template. Free: 3 analyses and 2 downloads / 30 days, no card. Pro €7/month. You supply the figures; we do not invent 140% of quota.',
        },
        { type: 'h2', text: 'Which number to use (if you lack “the official figure”)' },
        {
          type: 'p',
          text: 'Before: “Responsible for winning and retaining clients.” After: “Book of 45 B2B accounts (SaaS, 90-day cycle) in HubSpot; 112% of 2024 quota (internal, order of magnitude); 8 new hunter deals.” If you cannot name the exact %, use proxies: visits, demos/week, territory, customer type. You will have to hold it in interview.',
        },
        {
          type: 'ul',
          items: [
            'Quota or team ranking if it is defensible.',
            'Named CRM: Salesforce, HubSpot, Dynamics — the one in the ad if you used it.',
            'Hunter vs farmer vs inside: one word in the summary.',
            'Product: not “solutions”; the sector (industrial, insurance, SaaS).',
            'Sales language: [CEFR](/blog/idiomas-en-el-cv) if you have international customers.',
          ],
        },
        { type: 'h2', text: '“Sales star” Canva and the portal' },
        {
          type: 'p',
          text: 'A template with a sales bar chart is a drawing. ATS does not extract 112%. Put the number in text next to the role. One column. Canva guide if the PDF does not paste into Notepad.',
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'Quota has to be text, not a chart',
          lead: 'If your sales are a Canva drawing, the filter does not see them. Upload the CV and leave CRM and figures in words.',
          from: 'Sales chart',
          to: 'Quota and CRM as text',
          button: 'Rebuild my sales CV',
        },
        { type: 'h2', text: 'Tailor hunter and farmer' },
        {
          type: 'p',
          text: 'Do not use the same summary for a pure hunting vacancy and a KAM one. [Tailor for 20 minutes](/blog/adaptar-cv-a-una-oferta). Cut old retail roles if you are applying enterprise, or the reverse. Two pages max; a dense senior compresses 2010–2016.',
        },
        {
          type: 'faq',
          items: [
            {
              q: 'Can I name customers?',
              a: 'Only if the contract allows. If not: “IBEX retail” or “3 accounts >500 staff”.',
            },
            {
              q: 'Should I put variable pay?',
              a: 'Not on the PDF. In interview, yes. The file travels.',
            },
            {
              q: 'Inside sales and field on the same CV?',
              a: 'Yes, labelled. The ad usually wants one.',
            },
            {
              q: 'Does ClarCV calculate my quota?',
              a: 'No. It keeps what is in the file and a parseable layout.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
  },
  {
    slug: 'cv-auxiliar-administrativo',
    date: '2026-08-22',
    category: 'career',
    related: ['palabras-clave-cv-ats', 'cv-para-portales-empleo', 'que-no-poner-en-el-cv'],
    es: {
      title: 'CV de auxiliar administrativo: Excel y el archivo que parsea',
      description:
        'Reescribe el CV administrativo que ya tienes: software, volumen y un PDF que InfoJobs lee. Sin “tareas varias”. ClarCV.',
      readMinutes: 12,
      sections: [
        {
          type: 'p',
          text: '“Auxiliar administrativo” es un título que el ATS sí busca; “tareas varias de oficina” no. Quien busca este CV suele tener un Word de la ETT con foto y DNI. El portal pide Excel, SAP, facturación, atención telefónica, agenda. Esta guía no es un modelo en blanco: es cómo recortar el archivo que ya envías a InfoJobs o a la ETT.',
        },
        {
          type: 'p',
          text: 'Keywords: [palabras clave ATS](/blog/palabras-clave-cv-ats). Portales: [CV para InfoJobs y LinkedIn](/blog/cv-para-portales-empleo). DNI fuera: [qué no poner](/blog/que-no-poner-en-el-cv). ClarCV: subes PDF o Word, estructura, plantilla A4. Gratis: 3 análisis y 2 descargas / 30 días, sin tarjeta. Pro 7 €/mes.',
        },
        { type: 'h2', text: 'De “apoyo” a volumen y herramientas' },
        {
          type: 'p',
          text: 'Antes: “Apoyo administrativo al departamento y gestión de llamadas.” Después: “Facturación en Holded (~80 facturas/mes), archivo digital, centralita (~30 llamadas/día), Excel (tablas dinámicas para control de gastos).” El anuncio de auxiliar casi siempre lista un software: cópialo solo si lo usaste. ContaPlus, A3, SAP GUI, Outlook, Teams.',
        },
        {
          type: 'ul',
          items: [
            'Software de gestión y ofimática en texto, no “Office 90%”.',
            'Tipo de empresa: pyme, despacho, clínica, logística — cambia las keywords.',
            'Atención al público vs back office: una línea en el resumen.',
            'Idiomas de mostrador: MCER si la oferta es turística o internacional.',
            'Una página si la trayectoria es corta; dos si hay muchos contratos ETT — compacta los de un mes.',
          ],
        },
        {
          type: 'cta',
          variant: 'mid',
          title: '“Tareas varias” no pasa ningún filtro',
          lead: 'Sube el CV de la ETT. Quita el DNI, pon Excel y el programa de facturación en texto, regenera un PDF que el portal pueda leer.',
          from: 'Tareas varias',
          to: 'Excel + software en texto',
          button: 'Limpiar mi CV administrativo',
        },
        { type: 'h2', text: 'ETT, contratos cortos y el hueco' },
        {
          type: 'p',
          text: 'Agrupa misiones de la misma ETT (“varios clientes, 2023–2024: recepción, archivo, caja”) en lugar de diez bloques de 15 días. No mientas un indefinido. Si hay hueco, una línea: [hueco laboral](/blog/hueco-laboral-en-el-cv).',
        },
        {
          type: 'faq',
          items: [
            {
              q: '¿Pongo mecanografía?',
              a: 'Solo si el anuncio la pide. Si no, Excel pesa más.',
            },
            {
              q: '¿Foto en auxiliar?',
              a: 'En España a menudo sí; que no coma el email. Ver guía de foto.',
            },
            {
              q: '¿El carné de conducir?',
              a: 'Si el puesto es mensajería interna o varios centros. Si no, sobra.',
            },
            {
              q: '¿ClarCV rellena SAP si no lo usé?',
              a: 'No debe. Conserva lo del archivo.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
    en: {
      title: 'Admin assistant CV: Excel and a file that parses',
      description:
        'Rewrite the admin CV you already have: software, volume, and a PDF job boards can read. No “various office tasks”. ClarCV.',
      readMinutes: 12,
      sections: [
        {
          type: 'p',
          text: '“Administrative assistant” is a title ATS does search; “various office tasks” is not. People searching this CV often have a staffing-agency Word with a photo and ID. The board wants Excel, SAP, invoicing, phones, calendar. This guide is not a blank template: it is how to trim the file you already send to job boards or agencies.',
        },
        {
          type: 'p',
          text: 'Keywords: [ATS keywords](/blog/palabras-clave-cv-ats). Boards: [CVs for InfoJobs and LinkedIn](/blog/cv-para-portales-empleo). ID off: [what not to put](/blog/que-no-poner-en-el-cv). ClarCV: upload PDF or Word, structure, A4 template. Free: 3 analyses and 2 downloads / 30 days, no card. Pro €7/month.',
        },
        { type: 'h2', text: 'From “support” to volume and tools' },
        {
          type: 'p',
          text: 'Before: “Admin support to the department and call handling.” After: “Invoicing in Holded (~80 invoices/month), digital filing, switchboard (~30 calls/day), Excel (pivot tables for expense control).” Admin ads almost always list software: copy it only if you used it. ContaPlus, A3, SAP GUI, Outlook, Teams.',
        },
        {
          type: 'ul',
          items: [
            'Ops and office software as text, not “Office 90%”.',
            'Company type: SME, law firm, clinic, logistics — keywords change.',
            'Front desk vs back office: one line in the summary.',
            'Counter languages: CEFR if the ad is tourism or international.',
            'One page if the career is short; two if there are many agency contracts — compress the one-month ones.',
          ],
        },
        {
          type: 'cta',
          variant: 'mid',
          title: '“Various tasks” passes no filter',
          lead: 'Upload the agency CV. Drop the ID, put Excel and the invoicing tool as text, regenerate a PDF the board can read.',
          from: 'Various tasks',
          to: 'Excel + software as text',
          button: 'Clean my admin CV',
        },
        { type: 'h2', text: 'Agencies, short contracts, and gaps' },
        {
          type: 'p',
          text: 'Group missions from the same agency (“several clients, 2023–2024: reception, filing, till”) instead of ten 15-day blocks. Do not fake a permanent contract. If there is a gap, one line: [employment gap](/blog/hueco-laboral-en-el-cv).',
        },
        {
          type: 'faq',
          items: [
            {
              q: 'Should I list typing speed?',
              a: 'Only if the ad asks. If not, Excel weighs more.',
            },
            {
              q: 'Photo on an admin CV?',
              a: 'In Spain often yes; it must not eat the email. See the photo guide.',
            },
            {
              q: 'Driving licence?',
              a: 'If the role is internal courier or several sites. If not, skip it.',
            },
            {
              q: 'Does ClarCV add SAP if I never used it?',
              a: 'It must not. It keeps what is in the file.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
  },
  {
    slug: 'cv-para-profesor',
    date: '2026-08-22',
    category: 'career',
    related: ['cv-para-practicas', 'foto-en-el-cv', 'cv-para-oposiciones'],
    es: {
      title: 'CV de profesor o maestro: colegio vs oposiciones',
      description:
        'Dos intenciones: centro concertado/privado (PDF ATS) y oposición (pliego). Cómo reescribir el archivo que ya tienes. ClarCV.',
      readMinutes: 13,
      sections: [
        {
          type: 'p',
          text: '“CV de profesor” mezcla dos procesos. Un colegio concertado o privado usa portal o email: quiere especialidad, etapas, idiomas, innovación en viñetas cortas y un PDF que se abra. Una oposición sigue el temario y el sobre: el pliego manda, no Canva. Si envías el mismo Europass de cuatro páginas a los dos, pierdes el primero y no cumples el segundo. Esta guía parte del archivo que ya tienes y te obliga a elegir canal.',
        },
        {
          type: 'p',
          text: 'Prácticas y TFG: [CV para prácticas](/blog/cv-para-practicas). Europass: [cuándo dejarlo](/blog/europass-o-cv-moderno). Foto: [foto en el CV](/blog/foto-en-el-cv) (en educación en España suele esperarse). ClarCV sirve al PDF del centro privado/concertado, no sustituye el formato de una convocatoria pública. Gratis: 3 análisis y 2 descargas / 30 días, sin tarjeta. Pro 7 €/mes.',
        },
        { type: 'h2', text: 'Centro privado o concertado (el PDF del portal)' },
        {
          type: 'p',
          text: 'Resumen: etapa (Infantil, Primaria, ESO, FP), especialidad, idiomas MCER, una prueba (proyecto, tutoría, digital). Experiencia: centro, fechas, etapas, no “impartí clases”. Viñeta: “Tutor 5.º Primaria (2022–2025), 25 alumnos, coordinación con PT, Classroom + Additio.” Interinos: lista centros sin novelar cada suplencia de una semana — agrupa por curso escolar.',
        },
        {
          type: 'ul',
          items: [
            'Especialidad y cuerpo (si aplica) en texto, no solo en el máster.',
            'Competencia digital: herramientas reales (Moodle, Classroom), no “TICs”.',
            'Idiomas: B2/C1 si das bilingüe; no inflar. Guía MCER.',
            'Una página o dos. El TFM en dos líneas, no un abstract.',
          ],
        },
        { type: 'h2', text: 'Oposiciones: el pliego, no ClarCV como atajo' },
        {
          type: 'p',
          text: 'Si la convocatoria pide méritos en un modelo, instancia o sobre, ese documento gana. Un CV “moderno” no sustituye el anexo. Puedes tener un PDF corto para emails a centros mientras preparas oposición; no mezcles el temario de 70 temas en el archivo del concertado.',
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'El colegio no lee un Europass de cuatro páginas',
          lead: 'Si el centro pide CV por email o portal, regenera una o dos páginas parseables. Si es oposición, sigue el pliego — no lo sustituyas por Canva.',
          from: 'Europass largo',
          to: 'CV de centro, 1–2 páginas',
          button: 'Preparar el CV del centro',
        },
        { type: 'h2', text: 'Qué quitar del Word de la facultad' },
        {
          type: 'p',
          text: 'Objetivo genérico, DNI, diez cursos de 4 horas, hobbies. Deja especialidad, etapas, un proyecto y contacto. ClarCV regenera plantilla ATS para el canal privado. No genera el modelo de la consejería.',
        },
        {
          type: 'faq',
          items: [
            {
              q: '¿Mismo CV para pública interina y concertada?',
              a: 'Comparte hechos. El resumen y la extensión cambian. La interinidad agrupa centros; el concertado destaca proyecto de centro.',
            },
            {
              q: '¿Pongo el número de oposición?',
              a: 'En el PDF privado, no hace falta. En la instancia oficial, lo que pida el pliego.',
            },
            {
              q: '¿FP y universidad igual?',
              a: 'Cambia el vocabulario (módulo, ECTS, investigación). El ATS del centro sigue queriendo texto plano.',
            },
            {
              q: '¿ClarCV es de una consejería?',
              a: 'No. No sustituye convocatorias ni modelos oficiales.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
    en: {
      title: 'Teacher CV: school vs public exams',
      description:
        'Two intents: private/charter school (ATS PDF) vs public exam (the spec). How to rewrite the file you already have. ClarCV.',
      readMinutes: 13,
      sections: [
        {
          type: 'p',
          text: '“Teacher CV” mixes two processes. A private or state-funded independent school uses a portal or email: they want subject, stages, languages, innovation in short bullets, and a PDF that opens. A public teaching exam follows the syllabus and the envelope: the spec wins, not Canva. If you send the same four-page Europass to both, you lose the first and fail the second. This guide starts from the file you already have and makes you pick a channel.',
        },
        {
          type: 'p',
          text: 'Internships and dissertation: [internship CV](/blog/cv-para-practicas). Europass: [when to leave it](/blog/europass-o-cv-moderno). Photo: [photo on the CV](/blog/foto-en-el-cv) (often expected in Spanish education). ClarCV helps the private/charter PDF; it does not replace a public-call form. Free: 3 analyses and 2 downloads / 30 days, no card. Pro €7/month.',
        },
        { type: 'h2', text: 'Private or charter school (the portal PDF)' },
        {
          type: 'p',
          text: 'Summary: stage (early years, primary, secondary, VET), subject, CEFR languages, one proof (project, tutoring, digital). Experience: school, dates, stages, not “I taught classes”. Bullet: “Year 5 tutor (2022–2025), 25 pupils, SEN coordination, Classroom + Additio.” Supply teaching: list schools without novelising every one-week cover — group by school year.',
        },
        {
          type: 'ul',
          items: [
            'Subject and teaching body (if it applies) as text, not only in the master’s.',
            'Digital competence: real tools (Moodle, Classroom), not “ICT”.',
            'Languages: B2/C1 if you teach bilingual; do not inflate. CEFR guide.',
            'One page or two. The dissertation in two lines, not an abstract.',
          ],
        },
        { type: 'h2', text: 'Public exams: the spec, not ClarCV as a shortcut' },
        {
          type: 'p',
          text: 'If the call wants merits in a template, form, or envelope, that document wins. A “modern” CV does not replace the annex. You can keep a short PDF for emails to schools while you prepare the exam; do not dump a 70-topic syllabus into the charter-school file.',
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'The school does not read a four-page Europass',
          lead: 'If the school wants a CV by email or portal, regenerate one or two parseable pages. If it is a public exam, follow the spec — do not replace it with Canva.',
          from: 'Long Europass',
          to: 'School CV, 1–2 pages',
          button: 'Prepare the school CV',
        },
        { type: 'h2', text: 'What to cut from the faculty Word file' },
        {
          type: 'p',
          text: 'Generic objective, national ID, ten 4-hour courses, hobbies. Keep subject, stages, one project, and contact. ClarCV regenerates an ATS template for the private channel. It does not generate the regional-ministry form.',
        },
        {
          type: 'faq',
          items: [
            {
              q: 'Same CV for public supply and charter?',
              a: 'Share facts. Summary and length change. Supply work groups schools; charter highlights a school project.',
            },
            {
              q: 'Should I put the exam list number?',
              a: 'On the private PDF, no need. On the official form, whatever the spec asks.',
            },
            {
              q: 'VET and university the same?',
              a: 'Vocabulary changes (module, ECTS, research). The school ATS still wants plain text.',
            },
            {
              q: 'Is ClarCV a education-ministry tool?',
              a: 'No. It does not replace calls or official templates.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
  },
  {
    slug: 'cv-para-data-analyst',
    date: '2026-08-22',
    category: 'career',
    related: ['cv-para-tecnologia', 'palabras-clave-cv-ats', 'experiencia-laboral-con-logros'],
    es: {
      title: 'CV de data analyst: SQL, impacto y stack honesto',
      description:
        'Extiende el CV tech: SQL, BI y un logro con dataset. Reescribe el PDF que ya tienes, sin inflar Python. ClarCV.',
      readMinutes: 12,
      sections: [
        {
          type: 'p',
          text: 'El CV de data analyst se descarta por dos extremos: un listado de 30 herramientas o un resumen de “apasionado de los datos” sin SQL. El ATS de estas vacantes busca SQL, Python, Tableau o Power BI, dbt, el nombre del warehouse. El hiring manager busca un dominio (marketing, finanzas, ops) y un cambio medible. Esta página extiende [CV para developers, data y producto](/blog/cv-para-tecnologia) con el ángulo analista, no el de software engineer.',
        },
        {
          type: 'p',
          text: 'Logros: [métricas honestas](/blog/experiencia-laboral-con-logros). Keywords: [ATS](/blog/palabras-clave-cv-ats). ClarCV: subes el PDF, estructura, plantilla A4. Gratis: 3 análisis y 2 descargas / 30 días, sin tarjeta. Pro 7 €/mes. No inventamos un modelo de ML que no está en el archivo.',
        },
        { type: 'h2', text: 'Stack: lo de los últimos 24 meses' },
        {
          type: 'p',
          text: 'Antes: “Python, R, SQL, Hadoop, Spark, Excel, Word…” Después: “SQL (BigQuery), dbt, Looker, Python (pandas) para limpiezas puntuales, Excel.” Ortografía exacta: PostgreSQL, Power BI, Tableau. Si la oferta dice Looker y tú solo tienes Tableau, no lo sustituyas en el PDF; cuéntalo en entrevista como transferable.',
        },
        {
          type: 'ul',
          items: [
            'Una viñeta por logro: pregunta de negocio → dato → herramienta → decisión o ahorro.',
            'Escala: filas, eventos/día, número de dashboards en producción — sin secretos.',
            'No pongas “machine learning” por un curso de 10 h. Ver [cursos online](/blog/cursos-online-en-el-cv).',
            'GitHub: 1–2 repos con README, no 40 notebooks sin contexto.',
          ],
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'SQL tiene que estar en texto, no en un icono de ballena',
          lead: 'Si el stack es un mural de logos, el ATS no extrae BigQuery. Sube el CV y deja herramientas y un impacto en palabras.',
          from: 'Muro de logos',
          to: 'SQL + un dashboard en texto',
          button: 'Rehacer mi CV de datos',
        },
        { type: 'h2', text: 'Canva “data viz” y el portal' },
        {
          type: 'p',
          text: 'Un CV infográfico de analista es irónico: visualizas mal tu propio parseo. Una columna, skills en lista. El portfolio de Tableau Public va como URL en contacto. Prueba del bloc de notas.',
        },
        {
          type: 'faq',
          items: [
            {
              q: '¿Analyst y scientist en el mismo PDF?',
              a: 'Elige el anuncio. Un científico junior con SQL fuerte puede aplicar a analyst; no disfraces un analyst de research scientist.',
            },
            {
              q: '¿Pongo Kaggle?',
              a: 'Un ranking o un notebook con problema de negocio, sí. “Participé” sin enlace, no.',
            },
            {
              q: '¿Excel cuenta?',
              a: 'Sí, si el anuncio es de negocio. En un rol “modern data stack”, Excel es complemento, no el H1 de skills.',
            },
            {
              q: '¿ClarCV ejecuta tu SQL?',
              a: 'No. Solo el documento.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
    en: {
      title: 'Data analyst CV: SQL, impact, and an honest stack',
      description:
        'Extends the tech CV: SQL, BI, and one achievement with a dataset. Rewrite the PDF you already have, without inflating Python. ClarCV.',
      readMinutes: 12,
      sections: [
        {
          type: 'p',
          text: 'A data analyst CV dies at two extremes: a list of 30 tools, or a “passionate about data” summary with no SQL. ATS for these jobs looks for SQL, Python, Tableau or Power BI, dbt, the warehouse name. The hiring manager wants a domain (marketing, finance, ops) and a measurable change. This page extends [developer, data, and product CVs](/blog/cv-para-tecnologia) with the analyst angle, not the software-engineer one.',
        },
        {
          type: 'p',
          text: 'Achievements: [honest metrics](/blog/experiencia-laboral-con-logros). Keywords: [ATS](/blog/palabras-clave-cv-ats). ClarCV: upload the PDF, structure, A4 template. Free: 3 analyses and 2 downloads / 30 days, no card. Pro €7/month. We do not invent an ML model that is not in the file.',
        },
        { type: 'h2', text: 'Stack: the last 24 months' },
        {
          type: 'p',
          text: 'Before: “Python, R, SQL, Hadoop, Spark, Excel, Word…” After: “SQL (BigQuery), dbt, Looker, Python (pandas) for occasional cleaning, Excel.” Exact spelling: PostgreSQL, Power BI, Tableau. If the ad says Looker and you only have Tableau, do not swap it on the PDF; call it transferable in interview.',
        },
        {
          type: 'ul',
          items: [
            'One bullet per win: business question → data → tool → decision or saving.',
            'Scale: rows, events/day, dashboards in production — no secrets.',
            'Do not put “machine learning” for a 10-hour course. See [online courses](/blog/cursos-online-en-el-cv).',
            'GitHub: 1–2 repos with a README, not 40 context-free notebooks.',
          ],
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'SQL has to be text, not a whale icon',
          lead: 'If the stack is a logo mural, ATS will not extract BigQuery. Upload the CV and leave tools and one impact in words.',
          from: 'Logo wall',
          to: 'SQL + a dashboard in text',
          button: 'Rebuild my data CV',
        },
        { type: 'h2', text: '“Data viz” Canva and the portal' },
        {
          type: 'p',
          text: 'An infographic analyst CV is ironic: you visualise your own parsing badly. One column, skills as a list. A Tableau Public portfolio goes as a URL in contact. Notepad test.',
        },
        {
          type: 'faq',
          items: [
            {
              q: 'Analyst and scientist on the same PDF?',
              a: 'Pick the ad. A junior scientist with strong SQL can apply analyst; do not dress an analyst as a research scientist.',
            },
            {
              q: 'Should I put Kaggle?',
              a: 'A ranking or a notebook with a business problem, yes. “I took part” with no link, no.',
            },
            {
              q: 'Does Excel count?',
              a: 'Yes if the ad is business-facing. In a “modern data stack” role, Excel is a complement, not the skills H1.',
            },
            {
              q: 'Does ClarCV run your SQL?',
              a: 'No. Only the document.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
  },
  {
    slug: 'cv-para-hosteleria',
    date: '2026-08-22',
    category: 'career',
    related: ['cv-para-portales-empleo', 'cv-para-mercadona', 'enviar-cv-por-email'],
    es: {
      title: 'CV para hostelería: turnos, idiomas y un PDF que se abre',
      description:
        'No solo camarero: sala, cocina, pisos, eventos. Disponibilidad en texto y archivo ligero para ETT y hotel. ClarCV.',
      readMinutes: 12,
      sections: [
        {
          type: 'p',
          text: 'Un CV para hostelería se lee en el móvil del jefe de sala o en el ATS de una cadena. “Camarero responsable y dinámico” no es una keyword. Sí lo son: sala, barra, POS (Aloha, Gstock), inglés B1, turnos rotativos, banquetes, HACCP. Esta guía no es solo “CV camarero”: cubre cocina, pisos, recepción y eventos, reescribiendo el archivo que ya tienes — a menudo un Canva o un Word de la ETT.',
        },
        {
          type: 'p',
          text: 'Portales: [InfoJobs y LinkedIn](/blog/cv-para-portales-empleo). Email al hotel: [enviar CV por email](/blog/enviar-cv-por-email). Junior: [primer empleo](/blog/primer-empleo-cv). ClarCV: PDF parseable y ligero. Gratis: 3 análisis y 2 descargas / 30 días, sin tarjeta. Pro 7 €/mes.',
        },
        { type: 'h2', text: 'Disponibilidad y puesto, no el estado civil' },
        {
          type: 'p',
          text: 'Antes: “Busco trabajo en hostelería, disponible.” Después, en el resumen: “Camarero/a de sala, turnos rotativos y fines de semana, inglés B1, experiencia en terraza 80 cubiertos.” La disponibilidad es un hecho laboral. El estado civil no. [Qué no poner](/blog/que-no-poner-en-el-cv). Carné de manipulador: una línea con año si está vigente.',
        },
        {
          type: 'ul',
          items: [
            'Puesto concreto: sala, barra, cocina caliente, office, pisos, recepción, eventos.',
            'Volumen: cubiertos, habitaciones, tipo de servicio (menú, carta, banquete).',
            'Idiomas MCER: el turista se nota en la primera mesa.',
            'Software: TPV / PMS si lo usaste (no inventes Opera si solo viste la pantalla).',
            'Una página. Foto profesional reciente si el mercado local la espera; no un selfie de fiesta.',
          ],
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'El jefe de sala abre el PDF en el teléfono',
          lead: 'Si el archivo pesa 8 MB o es un Canva que no se selecciona, no llega. Regenera una página ligera con turnos e idiomas en texto.',
          from: 'Canva pesado',
          to: 'Una página que se abre',
          button: 'Aligerar mi CV de hostelería',
        },
        { type: 'h2', text: 'Temporada, ETT y huecos de invierno' },
        {
          type: 'p',
          text: 'Agrupa veranos (“temporada playa 2022–2025, mismo grupo hotelero”) en lugar de cuatro bloques idénticos. El invierno sin contrato: una línea, no un agujero. Ver hueco laboral. No disfraces un mes de extra de “chef ejecutivo”.',
        },
        {
          type: 'faq',
          items: [
            {
              q: '¿Pongo todas las barras donde eché un extra?',
              a: 'No. Agrupa extras. Destaca el local de más cubiertos o más idioma.',
            },
            {
              q: '¿El CV en inglés para un hotel de costa?',
              a: 'Si el anuncio está en inglés, sí. Si no, español con idiomas en MCER.',
            },
            {
              q: '¿Referencias del local?',
              a: 'Listas cuando las pidan, no en el PDF. Guía de referencias.',
            },
            {
              q: '¿ClarCV coloca en hoteles?',
              a: 'No. Prepara el archivo. El envío es tuyo.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
    en: {
      title: 'Hospitality CV: shifts, languages, and a PDF that opens',
      description:
        'Not only waiting tables: FOH, kitchen, housekeeping, events. Availability as text and a light file for agencies and hotels. ClarCV.',
      readMinutes: 12,
      sections: [
        {
          type: 'p',
          text: 'A hospitality CV is read on a floor manager’s phone or in a chain’s ATS. “Responsible, dynamic waiter” is not a keyword. These are: FOH, bar, POS (Aloha, Gstock), English B1, rotating shifts, banquets, HACCP. This guide is not only “waiter CV”: it covers kitchen, housekeeping, reception, and events, rewriting the file you already have — often Canva or an agency Word.',
        },
        {
          type: 'p',
          text: 'Boards: [InfoJobs and LinkedIn](/blog/cv-para-portales-empleo). Hotel email: [emailing your CV](/blog/enviar-cv-por-email). Junior: [first job](/blog/primer-empleo-cv). ClarCV: parseable, light PDF. Free: 3 analyses and 2 downloads / 30 days, no card. Pro €7/month.',
        },
        { type: 'h2', text: 'Availability and role, not marital status' },
        {
          type: 'p',
          text: 'Before: “Looking for hospitality work, available.” After, in the summary: “FOH waiter, rotating shifts and weekends, English B1, terrace of 80 covers.” Availability is a work fact. Marital status is not. [What not to put](/blog/que-no-poner-en-el-cv). Food-hygiene certificate: one line with year if it is valid.',
        },
        {
          type: 'ul',
          items: [
            'Specific role: FOH, bar, hot kitchen, kitchen porter, housekeeping, reception, events.',
            'Volume: covers, rooms, service type (set menu, à la carte, banquet).',
            'CEFR languages: tourists show at the first table.',
            'Software: POS / PMS if you used it (do not invent Opera if you only saw the screen).',
            'One page. Recent professional photo if the local market expects it; not a party selfie.',
          ],
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'The floor manager opens the PDF on a phone',
          lead: 'If the file is 8 MB or Canva that cannot be selected, it never arrives. Regenerate one light page with shifts and languages as text.',
          from: 'Heavy Canva',
          to: 'One page that opens',
          button: 'Lighten my hospitality CV',
        },
        { type: 'h2', text: 'Season, agencies, and winter gaps' },
        {
          type: 'p',
          text: 'Group summers (“beach season 2022–2025, same hotel group”) instead of four identical blocks. A winter without a contract: one line, not a hole. See employment gap. Do not dress a month as extra as “executive chef”.',
        },
        {
          type: 'faq',
          items: [
            {
              q: 'Should I list every bar I extraed in?',
              a: 'No. Group extras. Highlight the site with more covers or more language.',
            },
            {
              q: 'English CV for a coastal hotel?',
              a: 'If the ad is in English, yes. If not, Spanish with CEFR languages.',
            },
            {
              q: 'References from the venue?',
              a: 'Ready when asked, not on the PDF. References guide.',
            },
            {
              q: 'Does ClarCV place people in hotels?',
              a: 'No. It prepares the file. You send it.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
  },
  {
    slug: 'cv-freelance-o-autonomo',
    date: '2026-08-22',
    category: 'career',
    related: ['cambiar-de-sector-cv', 'experiencia-laboral-con-logros', 'linkedin-y-cv'],
    es: {
      title: 'CV de autónomo o freelance que busca un contrato',
      description:
        'Cómo traducir clientes y proyectos a experiencia que un ATS de empresa entiende, sin parecer “todo un poco”. ClarCV.',
      readMinutes: 12,
      sections: [
        {
          type: 'p',
          text: 'El CV de un autónomo que busca contrato indefinido choca con el parser: “Freelance 2019–actualidad” sin clientes parece un hueco. El reclutador corporativo teme que te vayas a tu cartera. La solución no es inventar un empleo en nómina. Es empaquetar proyectos como roles (cliente, fechas, stack, resultado) y un resumen que diga el puesto objetivo, no “multidisciplinar”.',
        },
        {
          type: 'p',
          text: 'Cambio de formato de carrera: [cambiar de sector](/blog/cambiar-de-sector-cv). Logros: [métricas](/blog/experiencia-laboral-con-logros). LinkedIn alineado: [LinkedIn y CV](/blog/linkedin-y-cv). ClarCV: un PDF de una columna. Gratis: 3 análisis y 2 descargas / 30 días, sin tarjeta. Pro 7 €/mes. No convertimos facturas en un cargo falso de “Head of”.',
        },
        { type: 'h2', text: 'Proyectos como experiencia (sin mentir el contrato)' },
        {
          type: 'p',
          text: 'Antes: “Autónomo. Diseño y marketing para diversos clientes.” Después: “Diseñador freelance (autónomo) — Cliente A (retail, 2023–2024): identidad y 40 piezas/mes en Figma, handoff a imprenta. Cliente B (SaaS, 6 meses): web en Webflow, +22% leads (dato del cliente).” Título: Freelance / autónomo. El cliente no es tu empleador si no lo fue; no pongas su logo como si fueras plantilla.',
        },
        {
          type: 'ul',
          items: [
            '3–6 proyectos fuertes, no 40. El resto en portfolio con URL.',
            'IVA y “CEO de mi SL” no ayudan al ATS de un puesto de especialista.',
            'Herramientas del anuncio si las facturaste de verdad.',
            'Disponibilidad: fecha de cierre de cartera o “aviso 30 días a clientes”.',
          ],
        },
        {
          type: 'cta',
          variant: 'mid',
          title: '“Diversos clientes” es un hueco para el ATS',
          lead: 'Nombra 3 proyectos con fechas y herramientas. Sube el PDF y regenera una experiencia que se pueda parsear.',
          from: 'Freelance genérico',
          to: 'Clientes y fechas en texto',
          button: 'Empaquetar mi trayectoria',
        },
        { type: 'h2', text: 'El miedo del hiring manager' },
        {
          type: 'p',
          text: 'Una línea en el resumen: “Busco contrato; cartera en cierre ordenado.” Eso no garantiza nada, pero evita el PDF que parece un folleto de agencia. No prometas exclusividad que no puedes cumplir.',
        },
        {
          type: 'faq',
          items: [
            {
              q: '¿Pongo la facturación anual?',
              a: 'Solo si es un imán y no rompe confidencialidad. Un % de un cliente, mejor en entrevista.',
            },
            {
              q: '¿Varios NIFs / marcas?',
              a: 'Un hilo de experiencia. Las marcas comerciales en una línea, no tres cabeceras.',
            },
            {
              q: '¿El CV y la web de freelance iguales?',
              a: 'La web puede ser creativa. El PDF del portal, parseable. Dos archivos.',
            },
            {
              q: '¿ClarCV emite facturas?',
              a: 'No. Solo el currículum.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
    en: {
      title: 'Freelance or self-employed CV when you want a contract',
      description:
        'How to turn clients and projects into experience a company ATS understands, without looking like “a bit of everything”. ClarCV.',
      readMinutes: 12,
      sections: [
        {
          type: 'p',
          text: 'A self-employed CV aimed at a permanent contract hits the parser: “Freelance 2019–present” with no clients looks like a gap. A corporate recruiter fears you will go back to your book. The fix is not inventing a payroll job. It is packing projects as roles (client, dates, stack, outcome) and a summary that names the target job, not “multidisciplinary”.',
        },
        {
          type: 'p',
          text: 'Career-format change: [career change](/blog/cambiar-de-sector-cv). Achievements: [metrics](/blog/experiencia-laboral-con-logros). LinkedIn aligned: [LinkedIn and CV](/blog/linkedin-y-cv). ClarCV: a one-column PDF. Free: 3 analyses and 2 downloads / 30 days, no card. Pro €7/month. We do not turn invoices into a fake “Head of” title.',
        },
        { type: 'h2', text: 'Projects as experience (without lying about the contract)' },
        {
          type: 'p',
          text: 'Before: “Self-employed. Design and marketing for various clients.” After: “Freelance designer (self-employed) — Client A (retail, 2023–2024): identity and 40 assets/month in Figma, print handoff. Client B (SaaS, 6 months): Webflow site, +22% leads (client figure).” Title: Freelance / self-employed. The client is not your employer if they were not; do not put their logo as if you were staff.',
        },
        {
          type: 'ul',
          items: [
            '3–6 strong projects, not 40. The rest on a portfolio URL.',
            'VAT and “CEO of my LTD” do not help an ATS for a specialist seat.',
            'Tools from the ad if you actually billed them.',
            'Availability: a date you wind down the book, or “30 days’ notice to clients”.',
          ],
        },
        {
          type: 'cta',
          variant: 'mid',
          title: '“Various clients” looks like a gap to ATS',
          lead: 'Name 3 projects with dates and tools. Upload the PDF and regenerate experience that can parse.',
          from: 'Generic freelance',
          to: 'Clients and dates as text',
          button: 'Pack my track record',
        },
        { type: 'h2', text: 'The hiring manager’s fear' },
        {
          type: 'p',
          text: 'One line in the summary: “Seeking a contract; book winding down in order.” That guarantees nothing, but it beats a PDF that looks like an agency leaflet. Do not promise exclusivity you cannot keep.',
        },
        {
          type: 'faq',
          items: [
            {
              q: 'Should I put annual billings?',
              a: 'Only if it is a magnet and does not break confidentiality. A % from one client is better in interview.',
            },
            {
              q: 'Several legal entities / brands?',
              a: 'One experience thread. Trading names in one line, not three headers.',
            },
            {
              q: 'Same CV and freelance site?',
              a: 'The site can be creative. The portal PDF must parse. Two files.',
            },
            {
              q: 'Does ClarCV issue invoices?',
              a: 'No. Only the CV.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
  },
];
